import { html, LitElement, TemplateResult } from "lit";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { StyleInfo, styleMap } from "lit/directives/style-map.js";
import { customElement, property, query, state } from "lit/decorators.js";
import {
  Sizes,
  SizeResolverService as sizeResolver,
} from "@slhuicore/utils/size.js";

import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { style } from "./assets/notification.generated-style";
import {
  EVENT_NOTIFICATION_CLOSED,
  ICON_HEIGHT,
  NotificationPosition,
  NotificationState,
} from "./notification.type";

/**
 * Notifications draw the user’s attention to warnings or important information such as status changes.
 * They can be assigned to certain elements or be universally valid.
 * The type and urgency of the information is conveyed by means of colouring and position.
 *
 * @CustomElement slh-notification
 *
 * @cssprop --slh-default-font-family - Default font family.
 * @cssprop --slh-color-neutral-shade-00 - Background color and icon color.
 * @cssprop --slh-color-neutral - Notification title font color.
 * @cssprop --slh-color-neutral-shade-60 - Notification content font color.
 * @cssprop --slh-color-info - Toast alert base color.
 * @cssprop --slh-color-error - Toast error base color.
 * @cssprop --slh-color-warning - Toast warning base color.
 * @cssprop --slh-color-success - Toast success base color.
 * @cssprop --slh-color-neutral-shade-40 - Toast close base color.
 * @cssprop --slh-base-xs-font-size - Notification font size.
 * @cssprop --slh-base-xxs-line-height - Notification line height.
 * @cssprop --slh-base-bold-font-weight - Notification title font weight.
 * @cssprop --slh-base-semi-bold-font-weight - Notification content font weight.
 * @cssprop --slh-color-neutral-shade-80 - Focused close icon color.
 * @cssprop --slh-color-neutral-shade-80-opacity-12 - Outer drop shadow color for notificaiton container.
 * @cssprop --slh-color-neutral-shade-80-opacity-16 - Inner Drop shadow color for notification container.
 * @cssprop --slh-color-primary - Link color.
 *
 * @slot default - Notification content
 * @slot title - Notification title
 */

@customElement("slh-notification")
export default class SlhNotification extends LitElement {
  @query(".notification-container", false)
  private notificationContainer!: HTMLElement;

  /**
   * Defines NotificationState
   * Default: info
   * Possible states are: info, warning, success and error
   */
  @property({
    type: NotificationState,
    attribute: "notification-state",
  })
  notificationState: NotificationState = NotificationState.INFO;

  /**
   * Defines position of the notification
   * Default position is TOP RIGHT
   * Possible positions are TOP_RIGHT, TOP_LEFT, BOTTOM_RIGHT, BOTTOM_LEFT
   * */
  @property({
    type: NotificationPosition,
  })
  position: NotificationPosition = NotificationPosition.TOP_RIGHT;

  /**
   * Duration of showed notification, notification will be hidden after time passes
   */
  @property({
    type: Number,
  })
  duration: number = 0;

  /**
   * If opened is set notification will be opened by default
   */
  @property({
    type: Boolean,
    attribute: true,
  })
  opened: boolean = false;

  /**
   * Top position of the notification
   */
  @property({
    type: Number,
    attribute: true,
  })
  top: number = 0;

  /**
   * Right position of the notification
   */
  @property({
    type: Number,
    attribute: true,
  })
  right: number = 0;

  /**
   * Bottom position of the notification
   */
  @property({
    type: Number,
    attribute: true,
  })
  bottom: number = 0;

  /**
   * Left position of the notification
   */
  @property({
    type: Number,
    attribute: true,
  })
  left: number = 0;

  /**
   * Used for setting id attribute value to the element, if not already set
   * @private
   */
  static instanceCount = 0;

  @state()
  private mobile: boolean = false;

  private onResize: (e: Event) => void = () => {};

  static styles = style;

  firstUpdated() {
    if (!this.hasAttribute("id")) {
      this.id = `slh-notification-${(SlhNotification.instanceCount += 1)}`;
    }

    this.onResize = this.updateMobileState.bind(this);
    window.addEventListener("resize", this.onResize);
    this.updateMobileState();

    if (this.opened) {
      this.showNotification();
    }
  }

  requestUpdate(name?: PropertyKey, oldValue?: unknown) {
    if (name === "opened" && this.opened) {
      this.showNotification();
    }
    super.requestUpdate(name, oldValue);
  }

  /**
   * Update mobile state prop
   */
  private updateMobileState(): void {
    sizeResolver.checkWindowSize();
    this.mobile = sizeResolver.getSize() < Sizes.SM;
  }

  private renderStyles(): StyleInfo {
    const styles: { [key: string]: string } = {};
    styles.top = this.isTopPositioned() ? `${this.top}px` : `unset`;
    styles.bottom = this.isBottomPositioned() ? `${this.bottom}px` : `unset`;
    if (this.mobile && this.isCenterPositioned()) {
      const margins =
        parseInt(String(this.left), 10) + parseInt(String(this.right), 10);
      styles.width = `calc(100% - ${margins}px)`;
      styles.left = this.left > 0 ? `${this.left}px` : `0`;
      styles.right = this.right > 0 ? `${this.right}px` : `0`;
    }
    if (!this.isCenterPositioned()) {
      styles.left = this.isLeftPositioned() ? `${this.left}px` : `unset`;
      styles.right = this.isRightPositioned() ? `${this.right}px` : `unset`;
    }
    return styles;
  }

  /**
   *
   * @returns {ClassInfo} ClassList
   */
  private renderClasses(): ClassInfo {
    return {
      "notification--info": this.notificationState === NotificationState.INFO,
      "notification--success":
        this.notificationState === NotificationState.SUCCESS,
      "notification--error": this.notificationState === NotificationState.ERROR,
      "notification--warning":
        this.notificationState === NotificationState.WARNING,
      "notification--centered": this.isCenterPositioned(),
      "notification--opened": this.opened,
    };
  }

  /**
   * @returns {TemplateResult} renders icon
   */
  private renderIcon(): TemplateResult {
    let icon = "";
    switch (this.notificationState) {
      case NotificationState.INFO:
        icon = IconType.ICO_INFO;
        break;
      case NotificationState.ERROR:
        icon = IconType.ICO_MISSINGINFO;
        break;
      case NotificationState.SUCCESS:
        icon = IconType.ICO_PASSED;
        break;
      default:
        icon = IconType.ICO_MISSINGINFO;
    }
    return html`<div class="icon-left">
      <slh-icon type="${icon}" height="${ICON_HEIGHT}"></slh-icon>
    </div>`;
  }

  /**
   *
   * @returns {TemplateResult} - renders close button
   */
  private renderCloseButton(): TemplateResult {
    return html`<button class="close-button" @click="${this.closeNotification}">
      <slh-icon type="ico_close" height="${ICON_HEIGHT}"></slh-icon>
    </button>`;
  }

  private renderContent(): TemplateResult {
    return html`
      ${this.renderIcon()}
      <div class="notification--text">
        <span class="notification--title"><slot name="title"></slot></span>
        <span class="notification--content"><slot></slot></span>
      </div>
      ${this.renderCloseButton()}
    `;
  }

  /**
   * handle action button click/enter
   * @param { PointerEvent} event
   */
  private handleActionButton(event: PointerEvent) {
    if (event.type === "click") {
      this.closeNotification();
    }
  }

  /**
   * Set accessibility for notification
   */
  private setAccessibility(): void {
    this.notificationContainer?.setAttribute("role", "alert");
    this.notificationContainer?.setAttribute("aria-live", "polite");
  }

  /**
   * Unset accessibility
   */
  private unsetAccessibility(): void {
    this.notificationContainer?.removeAttribute("role");
    this.notificationContainer?.removeAttribute("aria-live");
  }

  private isTopPositioned() {
    return (
      this.position.toUpperCase() === NotificationPosition.TOP_RIGHT ||
      this.position.toUpperCase() === NotificationPosition.TOP_LEFT ||
      this.position.toUpperCase() === NotificationPosition.TOP_CENTER
    );
  }

  private isBottomPositioned() {
    return (
      this.position.toUpperCase() === NotificationPosition.BOTTOM_RIGHT ||
      this.position.toUpperCase() === NotificationPosition.BOTTOM_LEFT ||
      this.position.toUpperCase() === NotificationPosition.BOTTOM_CENTER
    );
  }

  private isRightPositioned() {
    return (
      this.position.toUpperCase() === NotificationPosition.TOP_RIGHT ||
      this.position.toUpperCase() === NotificationPosition.BOTTOM_RIGHT
    );
  }

  private isLeftPositioned() {
    return (
      this.position.toUpperCase() === NotificationPosition.TOP_LEFT ||
      this.position.toUpperCase() === NotificationPosition.BOTTOM_LEFT
    );
  }

  private isCenterPositioned() {
    return (
      this.position.toUpperCase() === NotificationPosition.TOP_CENTER ||
      this.position.toUpperCase() === NotificationPosition.BOTTOM_CENTER
    );
  }

  /**
   * Display notification
   */
  private showNotification(): void {
    this.setAccessibility();
    if (this.duration) {
      this.closeTimeout();
    }
  }

  private closeTimeout() {
    setTimeout(() => {
      this.closeNotification();
    }, this.duration);
  }

  /**
   * hide notification
   */
  public closeNotification() {
    this.opened = false;
    this.unsetAccessibility();
    this.dispatchEvent(
      new CustomEvent(EVENT_NOTIFICATION_CLOSED, {
        detail: {
          id: this.id,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   *
   * @returns {TemplateResult} rendered notification
   */
  render(): TemplateResult {
    return html`<div
      class="slh-notification  notification--toast ${classMap(
        this.renderClasses()
      )}"
      style="${styleMap(this.renderStyles())}"
    >
      <div class="notification-container">
        <div class="notification-holder">${this.renderContent()}</div>
      </div>
    </div>`;
  }
}
